import { Component, Input, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { OnDestroy } from '@angular/core';
import { AppState } from 'src/app/store/app.states';
import { ComponentBase } from 'src/app/views/component-base';
import { commonState_IsSending, commonState_LatestJourney } from 'src/app/store/common/common.selectors';
import { GoToStepAction } from '../../actions';
import { AppButtonComponent, EntryFieldComponent, Helper } from '@ifaa-components/ui-components';
import { CheckMobileCodeModel, SendMobileCodeModel } from 'src/app/model/check-mobile-code.model';
import { mobileVerification_DataSaved, mobileVerification_Form, mobileVerification_IsMobileVerified, mobileVerification_SendMobileCodeMessage } from './selectors';
import { CheckMobileCodeRequestAction, ResetDataSavedAction, SendMobileCodeRequestAction } from './actions';
import { MobileVerificationModel } from './state';
import { FormGroupState, MarkAsTouchedAction, NgrxFormsModule } from 'ngrx-forms';
import { interval, take, timer } from 'rxjs';
import { AsyncPipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppButtonDirective } from 'src/app/directives/appbutton.directive';

@Component({
    selector: 'app-nmj-mobile-verification',
    templateUrl: './nmj-mobile-verification.component.html',
    styleUrls: ['./nmj-mobile-verification.component.scss'],
    imports: [
        AsyncPipe,
        AppButtonComponent,
        NgIf,
        NgClass,
        EntryFieldComponent,
        NgrxFormsModule,
        ReactiveFormsModule,
        FormsModule,
        NgTemplateOutlet,
        AppButtonDirective
    ],
})
export class NmjMobileVerificationComponent extends ComponentBase implements OnInit, OnDestroy {
    
    @Input() set step(value: number) {
        this.stepIndex = value;
    }

    isLoading$ = this.store.pipe(select(commonState_IsSending));
    journeySteps$ = this.store.pipe(select(commonState_LatestJourney));
    form$ = this.store.pipe(select(mobileVerification_Form));
    isMobileVerified$ = this.store.pipe(select(mobileVerification_IsMobileVerified));
    sendMobileCodeMessage$ = this.store.pipe(select(mobileVerification_SendMobileCodeMessage));
    dataSaved$ = this.store.pipe(select(mobileVerification_DataSaved));

    helper: Helper = new Helper();
    stepIndex: number = 0;
    nextStep: number = 0;
    previousStep: number = 0;
    resendLocked: boolean = false;
    resendLockedTime: number = 0;

    constructor(public store: Store<AppState>) {
        super();
    }

    async ngOnInit() {
        super.ngOnInitBase();

        var data = await this.helper.getValue(this.form$);
        this.store.dispatch(
            SendMobileCodeRequestAction({
                data: {
                    mobile: data.value.mobile,
                    forceResend: false,
                } as SendMobileCodeModel,
            })
        );

        this.sub = this.journeySteps$.subscribe(async x => {
            if (x) {
                this.nextStep = (await this.helper.getValue(this.journeySteps$)).journeySteps.filter(x => x.journeyStep > this.stepIndex)[0]?.journeyStep ?? -1;
                this.previousStep = (await this.helper.getValue(this.journeySteps$)).journeySteps.filter(x => x.journeyStep < this.stepIndex).sort((a, b) => b.journeyStep - a.journeyStep)[0].journeyStep;
            }
        })

        this.sub = this.dataSaved$.subscribe(async x => {
            if (x) {
                // go next step
                this.store.dispatch(GoToStepAction({ index: this.nextStep }));

                // reset data saved prop
                this.store.dispatch(ResetDataSavedAction());
            }
        })
    }

    ngOnDestroy(): void {
        super.ngOnDestroyBase();
    }

    async onCheckMobileCodeClick(
        form: FormGroupState<MobileVerificationModel>,
        code: string
    ) {
        if (form.isInvalid) {
            this.store.dispatch(new MarkAsTouchedAction(form.id));
            return;
        }

        this.store.dispatch(
            CheckMobileCodeRequestAction({
                data: {
                    code: code,
                } as CheckMobileCodeModel,
            })
        );
    }

    async onMoveBackClick() {
        this.store.dispatch(GoToStepAction({ index: this.previousStep }))
    }

    sendMobile(mobile: string) {
        if (this.resendLocked) return;
        this.store.dispatch(
            SendMobileCodeRequestAction({
                data: { mobile: mobile, forceResend: true } as SendMobileCodeModel,
            })
        );
        this.resendLocked = true;

        //countdown
        const timerInterval$ = interval(1000); //1s
        const timer$ = timer(30000); //30s
        const time = 30;
        this.resendLockedTime = time;

        const countDown$ = timerInterval$.pipe(take(time));
        this.sub = countDown$.subscribe(
            (val) => (this.resendLockedTime = time - val)
        );
        this.sub = timer$.subscribe((val) => {
            this.resendLocked = false;
        });
    }

}
